import { fn, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import sortBy from '@nullvoxpopuli/ember-composable-helpers/helpers/sort-by';
import { task, type Task } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import type Flow from 'garaje/models/flow';
import type IdScanField from 'garaje/models/id-scan-field';
import type IdScanPage from 'garaje/models/id-scan-page';
import type SignInField from 'garaje/models/sign-in-field';
import { SignInFieldKind } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';
import { filter, reads } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

interface SignInFieldsFieldOptionsModalIdScanningComponentSignature {
  Args: {
    signInFields?: Array<SignInField>;
    flow?: Flow;
    saveFieldsTask?: Task<void, []>;
    isDisabled?: boolean;
    isPristine?: boolean;
    onChange?: (field: SignInField, attr: string, value: unknown) => boolean | void;
  };
}

interface NonResourceOption {
  identifier: null;
  displayName?: string;
  disabled?: boolean;
}

const HIDDEN_FIELD_IDENTIFIERS = Object.freeze(['host', 'name']);
const NONE_OPTION: NonResourceOption = Object.freeze({ identifier: null, displayName: 'None' });
const LINE_OPTION: NonResourceOption = Object.freeze({ identifier: null, disabled: true });

const configurableFieldsFilterFn = (field: SignInField): boolean => {
  if (HIDDEN_FIELD_IDENTIFIERS.includes(field.identifier)) return false;

  // don't allow conditional fields to be mapped to ID scan values
  // Convert the length to a number to handle ComputedProperty type
  const length = Number(field.actionableSignInFields?.length || 0);
  if (length > 0) return false;

  return field.kind === SignInFieldKind.TEXT;
};

export default class SignInFieldsFieldOptionsModalIdScanningComponent extends Component<SignInFieldsFieldOptionsModalIdScanningComponentSignature> {
  @service declare store: Store;

  optionsName = 'id-scanning';

  @localCopy('args.signInFields', []) signInFields?: Array<SignInField>;

  @filter('signInFields', configurableFieldsFilterFn) declare configurableFields: Array<SignInField>;
  @reads('fetchIdScanFieldsTask.lastSuccessful.value') declare idScanFields: Array<IdScanField> | undefined;
  @reads('fetchIdScanPageTask.lastSuccessful.value') declare idScanPage: IdScanPage | undefined;

  get idScanFieldOptions(): Array<IdScanField | NonResourceOption> {
    const { idScanFields = [] } = this;
    const sortedIdScanFields = [...idScanFields].sort((a, b) => `${a.displayName}`.localeCompare(`${b.displayName}`));

    return [NONE_OPTION, LINE_OPTION, ...sortedIdScanFields];
  }

  get isCTAVisible(): boolean {
    if (this.args.isDisabled) return false;

    return this.isNotEmpty;
  }

  get isCTADisabled(): boolean {
    return this.args.isDisabled || this.args.isPristine || this.saveIdScanFieldsTask.isRunning;
  }

  get isNotEmpty(): boolean {
    return this.configurableFields.length > 0;
  }

  get isDropdownDisabled(): boolean {
    return this.args.isDisabled || this.saveIdScanFieldsTask.isRunning;
  }

  constructor(owner: unknown, args: SignInFieldsFieldOptionsModalIdScanningComponentSignature['Args']) {
    super(owner, args);
    void this.fetchAllDataTask.perform();
  }

  onChange(field: SignInField, attr: string, value: unknown): boolean | void {
    if (typeof this.args.onChange !== 'function') return true;
    return this.args.onChange(field, attr, value);
  }

  @action
  setIdScanField(field: SignInField, selection: IdScanField): void {
    if (this.onChange(field, 'idScanField', selection.identifier) === false) return;
    field.idScanField = selection.identifier;
  }

  fetchAllDataTask = task(
    { drop: true },
    async (): Promise<{ idScanFields: RecordArray<IdScanField>; idScanPage: IdScanPage | undefined }> => {
      const idScanFields = await this.fetchIdScanFieldsTask.perform();
      const idScanPage = await this.fetchIdScanPageTask.perform();

      return { idScanFields, idScanPage };
    },
  );

  fetchIdScanFieldsTask = task({ drop: true }, async (): Promise<RecordArray<IdScanField>> => {
    let idScanFields = this.store.peekAll('id-scan-field');

    if (!idScanFields.length) {
      idScanFields = (await this.store.findAll('id-scan-field')) as RecordArray<IdScanField>;
    }

    return idScanFields;
  });

  fetchIdScanPageTask = task({ drop: true }, async (): Promise<IdScanPage | undefined> => {
    const idScanPage = await this.args.flow?.idScanPage;

    return idScanPage;
  });

  saveIdScanFieldsTask = task(async (): Promise<void> => {
    await this.args.saveFieldsTask?.perform();
  });

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Automatically fill sign-in fields in Envoy with information pulled from scanned visitor IDs. This works best
        with hidden and optional fields for visitors. Learn more about
        <a href="https://envoy.help/en/articles/3444461-id-scanning" target="_blank" rel="noopener">
          ID Scanning.
        </a>
      </p>
      {{#if this.fetchAllDataTask.isRunning}}
        <p class="text-center py-18">
          <img src="/assets/images/progress-small-gray.svg" alt="loading" />
        </p>
      {{else if this.idScanPage.enabled}}
        {{#if this.isNotEmpty}}
          <div class="mb-8">
            <div data-test-field-list class="mt-6">
              <div class="flex text-carbon-50 font-bold">
                <span class="flex-[3] text-sm">Field</span>
                <span class="w-16 mx-4"></span>
                <span id="identificationFieldConfigurationHeading" class="w-36 md:w-48 lg:w-64 text-sm text-left">
                  Identification field
                </span>
              </div>

              <hr class="my-2" />

              <div class="overflow-y-auto overscroll-y-contain max-h-[40vh]">
                {{#each (sortBy "position" this.configurableFields) as |field|}}
                  <div data-test-configurable-field={{field.id}} class="flex items-center mt-2 mb-6">
                    <p
                      id={{concat "identificationFieldConfigurationFieldName-" field.id}}
                      class="flex-[3] text-sm text-carbon-50 m-0"
                    >
                      {{field.name}}
                    </p>

                    <div class="w-16 mx-4">
                      <hr class="w-16" />
                    </div>

                    <div class="w-36 md:w-48 lg:w-64" data-test-configurable-field-id-scan={{field.id}}>
                      <PowerSelect
                        @triggerClass="!m-0 w-full"
                        @destination="modal-overlays"
                        @selected={{findBy "identifier" field.idScanField this.idScanFieldOptions}}
                        @options={{this.idScanFieldOptions}}
                        @searchEnabled={{false}}
                        @disabled={{this.isDropdownDisabled}}
                        @onChange={{fn this.setIdScanField field}}
                        as |idScanField|
                      >
                        {{#if idScanField.displayName}}
                          <span data-test-select-option>{{idScanField.displayName}}</span>
                        {{else}}
                          <hr />
                        {{/if}}
                      </PowerSelect>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          </div>

          {{#if this.isCTAVisible}}
            <button
              disabled={{this.isCTADisabled}}
              class="btn btn--small btn--brand float-right mb-8 {{if this.isCTADisabled 'disabled'}} "
              {{on "click" this.saveIdScanFieldsTask.perform}}
              data-test-save-options={{this.optionsName}}
            >
              {{#if this.saveIdScanFieldsTask.isRunning}}
                Saving...
              {{else}}
                Save
              {{/if}}
            </button>
          {{/if}}
        {{else}}
          <p data-test-empty-text class="text-center text-carbon-40 mt-12 mb-24">
            You have no fields to
            {{if @isDisabled "view" "manage"}}.
          </p>
        {{/if}}
      {{else}}
        {{#let
          (if
            @flow.isGlobal
            "location-overview.sign-in-flows.show.security"
            "visitors.settings.visitor-types.flow.security"
          )
          as |securityRoute|
        }}
          <p data-test-id-scanning-not-enabled class="text-center text-carbon-40 mt-12 mb-24">
            To use this feature, you need to create short answer sign-in fields and turn on
            <LinkTo @route={{securityRoute}} @model={{@flow.id}} data-test-goto-flow-security>
              ID scanning →
            </LinkTo>
          </p>
        {{/let}}
      {{/if}}
    </div>
  </template>
}
